import Swal from 'sweetalert2';
import getGraphQlClient from '~graphql/getGraphQlClient';
import { onErrorShowMessage } from '~src/errors';
import { FEATURES, isFeatureEnabled } from '~src/features';
import { isLoggedUserSuperUser } from '~src/user';
import {
  DELETE_TEMPLATE,
  DELETE_TEMPLATE_DEFAULT,
  DELETE_TEMPLATE_DEFAULT_PAGE,
  DELETE_TEMPLATE_PAGE,
} from '~graphql/queries/modifyTemplate';
import {
  GET_TEMPLATE_BY_ID,
  GET_TEMPLATE_DEFAULT_BY_ID,
} from '~graphql/queries/editorTemplates';

const getTemplateId = (templateOrId) =>
  typeof templateOrId === 'string' ? templateOrId : templateOrId?.objectId;

const getTemplateName = (templateOrId) =>
  typeof templateOrId === 'string'
    ? 'questo modello'
    : templateOrId?.name || 'questo modello';

const getPageConnection = (template, isGlobal) =>
  isGlobal
    ? template?.editor_templates_default_pages
    : template?.editor_templates_pages;

const getTemplatePageIds = (template, isGlobal) => {
  const pages = getPageConnection(template, isGlobal);

  if (Array.isArray(pages)) {
    return pages.map((page) => page.objectId).filter(Boolean);
  }

  return (pages?.edges || [])
    .map((edge) => edge.node?.objectId)
    .filter(Boolean);
};

const fetchTemplateForDelete = async (templateId, isGlobal) => {
  const graphQlClient = await getGraphQlClient();
  const response = await graphQlClient.query({
    fetchPolicy: 'network-only',
    variables: { id: templateId },
    query: isGlobal ? GET_TEMPLATE_DEFAULT_BY_ID : GET_TEMPLATE_BY_ID,
  });

  return isGlobal
    ? response.data?.getEditorTemplatesDefault
    : response.data?.getEditorTemplates;
};

const canDeleteTemplates = async () => {
  const isSuperUser = await isLoggedUserSuperUser();
  return isSuperUser || (await isFeatureEnabled(FEATURES.DELETE_TEMPLATES));
};

const deleteTemplatePages = async (graphQlClient, pageIds, isGlobal) => {
  const mutation = isGlobal
    ? DELETE_TEMPLATE_DEFAULT_PAGE
    : DELETE_TEMPLATE_PAGE;

  await Promise.all(
    pageIds.map((id) =>
      graphQlClient.mutate({
        mutation,
        variables: { id },
      })
    )
  );
};

export const deleteTemplate = async (
  templateOrId,
  isGlobal = false,
  { showSuccess = true } = {}
) => {
  const templateId = getTemplateId(templateOrId);
  if (!templateId) return false;

  if (!(await canDeleteTemplates())) {
    await Swal.fire({
      icon: 'warning',
      title: 'Azione non disponibile',
      text: 'Non hai i permessi per eliminare questo modello.',
      confirmButtonColor: '#278e8e',
    });
    return false;
  }

  if (window.editorLoader?.templateId === templateId) {
    await Swal.fire({
      icon: 'warning',
      title: 'Modello aperto',
      text:
        'Non puoi eliminare il modello che stai modificando. Torna alla libreria e riprova.',
      confirmButtonColor: '#278e8e',
    });
    return false;
  }

  const result = await Swal.fire({
    icon: 'warning',
    title: 'Eliminare il modello?',
    text: `Il modello "${getTemplateName(
      templateOrId
    )}" non sarà più disponibile in libreria.`,
    showCancelButton: true,
    confirmButtonText: 'Elimina',
    cancelButtonText: 'Annulla',
    confirmButtonColor: '#c63e3a',
  });

  if (!result.isConfirmed) return false;

  Swal.fire({
    title: 'Eliminazione modello',
    text: 'Sto eliminando il modello...',
    allowOutsideClick: false,
    allowEscapeKey: false,
    allowEnterKey: false,
    showConfirmButton: false,
    willOpen: () => Swal.showLoading(),
  });

  try {
    const graphQlClient = await getGraphQlClient();
    let template = templateOrId;

    if (
      typeof templateOrId === 'string' ||
      getTemplatePageIds(templateOrId, isGlobal).length === 0
    ) {
      template = await fetchTemplateForDelete(templateId, isGlobal);
    }

    await deleteTemplatePages(
      graphQlClient,
      getTemplatePageIds(template, isGlobal),
      isGlobal
    );
    await graphQlClient.mutate({
      mutation: isGlobal ? DELETE_TEMPLATE_DEFAULT : DELETE_TEMPLATE,
      variables: { id: templateId },
    });

    if (showSuccess) {
      await Swal.fire({
        icon: 'success',
        title: 'Eliminato',
        text: 'Il modello è stato eliminato.',
        confirmButtonColor: '#278e8e',
      });
    } else {
      Swal.close();
    }

    return true;
  } catch (error) {
    console.error(error);
    await onErrorShowMessage(false, {
      text: 'Non è stato possibile eliminare il modello.',
    });
    return false;
  }
};
